import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getVenue, updateVenue, uploadImage, uploadVideo, checkDns, checkVenueSlug } from "@/api/endpoints";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { ImageCropDialog } from "@/components/image-crop-dialog";
import { Upload, Save, Volume2, Globe, CheckCircle2, XCircle, Copy, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useRef } from "react";
import { playSound, ORDER_SOUNDS, WAITER_SOUNDS } from "@/utils/notification-sounds";
import type { OrderSoundKey, WaiterSoundKey } from "@/utils/notification-sounds";

export function VenueDetailPage() {
  const { venueId } = useParams<{ venueId: string }>();
  const qc = useQueryClient();
  const { t } = useI18n();

  const { data: venue, isLoading } = useQuery({
    queryKey: ["venue", venueId],
    queryFn: () => getVenue(venueId!),
    enabled: !!venueId,
  });

  const [form, setForm] = useState({
    name: "",
    slug: "",
    description: "",
    address: "",
    phone: "",
    website: "",
    instagram: "",
    currency: "TRY",
  });
  // Slug uygunluk durumu — input'a yazılırken canlı kontrol için.
  const [slugCheck, setSlugCheck] = useState<{
    status: "idle" | "checking" | "ok" | "taken" | "reserved" | "too_short";
    normalized?: string;
  }>({ status: "idle" });
  const [customDomain, setCustomDomain] = useState("");
  const [dnsStatus, setDnsStatus] = useState<"idle" | "checking" | "ok" | "fail">("idle");
  const [idCopied, setIdCopied] = useState(false);
  const [waiterCallEnabled, setWaiterCallEnabled] = useState(true);
  const [orderEnabled, setOrderEnabled] = useState(true);
  const [loyaltyEnabled, setLoyaltyEnabled] = useState(true);
  const [reviewsEnabled, setReviewsEnabled] = useState(true);
  const [waiterSound, setWaiterSound] = useState<WaiterSoundKey>("bell");
  const [orderSound, setOrderSound] = useState<OrderSoundKey>("kaching");

  // Crop dialog state
  const [cropSrc, setCropSrc] = useState("");
  const [cropType, setCropType] = useState<"logo" | "cover" | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (venue) {
      setForm({
        name: venue.name,
        slug: venue.slug,
        description: venue.description ?? "",
        address: venue.address ?? "",
        phone: venue.phone ?? "",
        website: venue.website ?? "",
        instagram: venue.instagram ?? "",
        currency: venue.currency,
      });
      setCustomDomain(venue.customDomain ?? "");
      setWaiterCallEnabled((venue as unknown as Record<string, unknown>).waiterCallEnabled !== false);
      setOrderEnabled((venue as unknown as Record<string, unknown>).orderEnabled !== false);
      setLoyaltyEnabled((venue as unknown as Record<string, unknown>).loyaltyEnabled !== false);
      setReviewsEnabled((venue as unknown as Record<string, unknown>).reviewsEnabled !== false);
      const sounds = (venue as unknown as Record<string, unknown>).notificationSounds as Record<string, string> | undefined;
      if (sounds?.waiterSound) setWaiterSound(sounds.waiterSound as WaiterSoundKey);
      if (sounds?.orderSound) setOrderSound(sounds.orderSound as OrderSoundKey);
    }
  }, [venue]);

  // Slug input'una yazılınca 500ms debounce + backend canlı kontrol.
  // Kendi mevcut slug'ı "kullanılıyor" görünmesin diye `exclude: venueId`.
  useEffect(() => {
    if (!form.slug || form.slug === venue?.slug) {
      setSlugCheck({ status: "idle" });
      return;
    }
    setSlugCheck({ status: "checking" });
    const handle = setTimeout(async () => {
      try {
        const r = await checkVenueSlug(form.slug, venueId);
        if (r.available) {
          setSlugCheck({ status: "ok", normalized: r.normalized });
        } else if (r.reason === "too_short") {
          setSlugCheck({ status: "too_short" });
        } else if (r.reason === "reserved") {
          setSlugCheck({ status: "reserved" });
        } else {
          setSlugCheck({ status: "taken" });
        }
      } catch {
        setSlugCheck({ status: "idle" });
      }
    }, 500);
    return () => clearTimeout(handle);
  }, [form.slug, venue?.slug, venueId]);

  const updateMutation = useMutation({
    mutationFn: (data: typeof form) => updateVenue(venueId!, {
      ...data,
      customDomain: customDomain,
      waiterCallEnabled,
      orderEnabled,
      loyaltyEnabled,
      reviewsEnabled,
      notificationSounds: { waiterSound, orderSound },
    } as Parameters<typeof updateVenue>[1]),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["venue", venueId] });
      qc.invalidateQueries({ queryKey: ["venues"] });
    },
  });

  const [uploadError, setUploadError] = useState("");

  const logoMutation = useMutation({
    mutationFn: async (file: File) => {
      const url = await uploadImage(file, "logo", venueId!);
      await updateVenue(venueId!, { logo: url });
      return url;
    },
    onSuccess: () => {
      setUploadError("");
      qc.invalidateQueries({ queryKey: ["venue", venueId] });
    },
    onError: (err: Error) => setUploadError(`Logo yuklenemedi: ${err.message}`),
  });

  const coverMutation = useMutation({
    mutationFn: async (file: File) => {
      const url = await uploadImage(file, "cover", venueId!);
      await updateVenue(venueId!, { coverImage: url });
      return url;
    },
    onSuccess: () => {
      setUploadError("");
      qc.invalidateQueries({ queryKey: ["venue", venueId] });
    },
    onError: (err: Error) => setUploadError(`Kapak gorseli yuklenemedi: ${err.message}`),
  });

  const [videoUploading, setVideoUploading] = useState(false);
  const [videoCacheBust, setVideoCacheBust] = useState(0);

  const videoMutation = useMutation({
    mutationFn: async (file: File) => {
      setVideoUploading(true);
      try {
        const url = await uploadVideo(file, venueId!);
        await updateVenue(venueId!, { coverVideo: url } as Parameters<typeof updateVenue>[1]);
        return url;
      } finally {
        setVideoUploading(false);
      }
    },
    onSuccess: () => {
      setUploadError("");
      setVideoCacheBust(Date.now());
      qc.invalidateQueries({ queryKey: ["venue", venueId] });
    },
    onError: (err: Error) => setUploadError(`Video yuklenemedi: ${err.message}`),
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, type: "logo" | "cover") => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setCropSrc(reader.result as string);
      setCropType(type);
    };
    reader.readAsDataURL(file);
    e.target.value = ""; // Reset so same file can be selected again
  };

  const handleCropped = (file: File) => {
    if (cropType === "logo") {
      logoMutation.mutate(file);
    } else if (cropType === "cover") {
      coverMutation.mutate(file);
    }
  };

  if (isLoading) {
    return <div className="py-8 text-center text-muted-foreground">{t("loading")}</div>;
  }

  if (!venue) {
    return <div className="py-8 text-center text-muted-foreground">{t("venue_not_found")}</div>;
  }

  return (
    <div className="max-w-2xl">
      <h1 className="mb-6 text-2xl font-bold">{t("venue_info")}</h1>

      {/* İşletme No — entegrasyon başvuruları ve destek için */}
      <Card className="mb-6">
        <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-6">
          <div>
            <p className="text-sm font-medium">İşletme No</p>
            <p className="text-xs text-muted-foreground">
              Getir / Trendyol / Yemeksepeti başvurularında ve destekle iletişimde bu numarayı kullanın.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <code className="rounded-md bg-muted px-2 py-1.5 text-sm font-mono">{venueId}</code>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                await navigator.clipboard.writeText(venueId ?? "");
                setIdCopied(true);
                setTimeout(() => setIdCopied(false), 1500);
              }}
            >
              {idCopied ? <CheckCircle2 className="size-4 text-emerald-600" /> : <Copy className="size-4" />}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Logo & Cover */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row">
        <Card className="flex-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t("logo")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {venue.logo ? (
                <img src={venue.logo} alt="Logo" className="size-16 rounded-xl object-cover" />
              ) : (
                <div className="flex size-16 items-center justify-center rounded-xl bg-muted text-2xl font-bold text-muted-foreground">
                  {venue.name[0]}
                </div>
              )}
              <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFileSelect(e, "logo")} />
              <Button variant="outline" size="sm" className="w-full" onClick={() => logoInputRef.current?.click()} disabled={logoMutation.isPending}>
                {logoMutation.isPending ? (
                  <><svg className="mr-2 size-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>{t("uploading")}</>
                ) : (
                  <><Upload className="mr-2 size-4" />{t("upload")}</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="flex-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t("cover_image")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {venue.coverImage ? (
                <img src={venue.coverImage} alt="Cover" className="h-16 w-full rounded-xl object-cover" />
              ) : (
                <div className="flex h-16 w-full items-center justify-center rounded-xl bg-muted text-xs text-muted-foreground">
                  {t("no_image")}
                </div>
              )}
              <input ref={coverInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFileSelect(e, "cover")} />
              <Button variant="outline" size="sm" className="w-full" onClick={() => coverInputRef.current?.click()} disabled={coverMutation.isPending}>
                {coverMutation.isPending ? (
                  <><svg className="mr-2 size-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>{t("uploading")}</>
                ) : (
                  <><Upload className="mr-2 size-4" />{t("upload")}</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="flex-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t("cover_video")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(venue as unknown as Record<string, string>).coverVideo ? (
                <video key={videoCacheBust} src={`${(venue as unknown as Record<string, string>).coverVideo}?t=${videoCacheBust}`} className="h-16 w-full rounded-xl object-cover" muted />
              ) : (
                <div className="flex h-16 w-full items-center justify-center rounded-xl bg-muted text-xs text-muted-foreground">
                  {t("no_video")}
                </div>
              )}
              <input ref={videoInputRef} type="file" accept="video/mp4,video/webm" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; if (file) videoMutation.mutate(file); e.target.value = ""; }} />
              <Button variant="outline" size="sm" className="w-full" onClick={() => videoInputRef.current?.click()} disabled={videoUploading}>
                {videoUploading ? (
                  <>
                    <svg className="mr-2 size-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                    {t("uploading")}
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 size-4" />
                    {t("upload")}
                  </>
                )}
              </Button>
              <p className="text-xs text-muted-foreground">MP4 / WebM, max 20MB</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {uploadError && (
        <p className="mb-4 text-sm text-destructive">{uploadError}</p>
      )}

      {/* Custom Domain */}
      <Card className="mb-6">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm">
            <Globe className="size-4" />
            {t("custom_domain")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">{t("custom_domain_desc")}</p>
          <div>
            <Label>{t("domain")}</Label>
            <Input
              value={customDomain}
              onChange={(e) => setCustomDomain(e.target.value.toLowerCase().trim())}
              placeholder={`menu.${(venue.slug || "isletme")}.com`}
            />
            <p className="text-xs text-muted-foreground mt-1">{t("custom_domain_hint").replace("{{slug}}", venue.slug)}</p>
            {customDomain && !/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(customDomain) && (
              <p className="text-xs text-destructive mt-1">{t("custom_domain_invalid")}</p>
            )}
          </div>
          {customDomain && /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(customDomain) && (
            <div className="rounded-lg border bg-muted/50 p-3 space-y-3">
              <p className="text-sm font-medium">{t("dns_setup")}</p>
              <ol className="list-decimal list-inside space-y-1.5 text-xs text-muted-foreground">
                <li>{t("dns_step_1")}</li>
                <li>{t("dns_step_2")}</li>
                <li>{t("dns_step_3")}</li>
              </ol>
              <div>
                <p className="text-xs font-medium mb-1">{t("dns_record")}</p>
                <div className="flex items-center gap-2 rounded bg-background border px-3 py-2">
                  <div className="flex-1 grid grid-cols-3 gap-2 text-xs font-mono">
                    <span className="text-muted-foreground">{t("dns_type")}: <strong>CNAME</strong></span>
                    <span className="text-muted-foreground">{t("dns_name")}: <strong>{customDomain.split('.')[0]}</strong></span>
                    <span className="text-muted-foreground">{t("dns_value")}: <strong>menu.heremenu.app</strong></span>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-6"
                    onClick={() => navigator.clipboard.writeText(`${customDomain} CNAME menu.heremenu.app`)}
                  >
                    <Copy className="size-3" />
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">{t("dns_setup_note")}</p>
            </div>
          )}
          {venue.customDomain && (
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                disabled={dnsStatus === "checking"}
                onClick={async () => {
                  setDnsStatus("checking");
                  try {
                    const res = await checkDns(venueId!);
                    setDnsStatus(res.ok ? "ok" : "fail");
                  } catch {
                    setDnsStatus("fail");
                  }
                }}
              >
                <RefreshCw className={`mr-1.5 size-3.5 ${dnsStatus === "checking" ? "animate-spin" : ""}`} />
                {dnsStatus === "checking" ? t("dns_checking") : t("dns_check")}
              </Button>
              {dnsStatus === "ok" && (
                <span className="flex items-center gap-1 text-xs text-green-600">
                  <CheckCircle2 className="size-3.5" />
                  {t("dns_ok")}
                </span>
              )}
              {dnsStatus === "fail" && (
                <span className="flex items-center gap-1 text-xs text-destructive">
                  <XCircle className="size-3.5" />
                  {t("dns_fail")}
                </span>
              )}
            </div>
          )}
          {venue.customDomain && (venue as unknown as Record<string, unknown>).customDomainStatus === "pending" && (
            <p className="text-xs text-amber-600">{t("domain_pending")}</p>
          )}
          {venue.customDomain && (venue as unknown as Record<string, unknown>).customDomainStatus === "active" && (
            <p className="flex items-center gap-1 text-xs text-green-600">
              <CheckCircle2 className="size-3.5" />
              {t("domain_active")}
            </p>
          )}
          {venue.customDomain && (venue as unknown as Record<string, unknown>).customDomainStatus === "failed" && (
            <p className="flex items-center gap-1 text-xs text-destructive">
              <XCircle className="size-3.5" />
              {t("domain_failed")}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Form */}
      <Card>
        <CardContent className="pt-6">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              updateMutation.mutate(form);
            }}
            className="space-y-4"
          >
            <div>
              <Label>{t("venue_name")} <span className="text-destructive">*</span></Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>

            {/* Menü adresi (slug) — kullanıcı edit edebilir.
                Format: menu.heremenu.app/<slug>. Backend slugify ile
                normalize eder; canlı endpoint /venues/check-slug ile
                500 ms debounced uygunluk gösterir. */}
            <div>
              <Label>Menü adresi</Label>
              <div className="flex items-stretch overflow-hidden rounded-md border bg-background">
                <span className="flex items-center bg-muted px-3 text-sm text-muted-foreground">
                  menu.heremenu.app/
                </span>
                <Input
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                  placeholder="ornek-isletme"
                />
              </div>
              <p className="mt-1 text-xs">
                {slugCheck.status === "checking" && (
                  <span className="text-muted-foreground">Kontrol ediliyor…</span>
                )}
                {slugCheck.status === "ok" && (
                  <span className="text-emerald-600">
                    ✓ Kullanılabilir — menu.heremenu.app/{slugCheck.normalized}
                  </span>
                )}
                {slugCheck.status === "taken" && (
                  <span className="text-destructive">
                    ✗ Bu adres başka bir işletme tarafından kullanılıyor.
                  </span>
                )}
                {slugCheck.status === "reserved" && (
                  <span className="text-destructive">
                    ✗ Bu adres rezerve, başka bir tane seçin.
                  </span>
                )}
                {slugCheck.status === "too_short" && (
                  <span className="text-destructive">
                    ✗ En az 2 karakter olmalı.
                  </span>
                )}
                {slugCheck.status === "idle" && (
                  <span className="text-muted-foreground">
                    Sadece harf, rakam ve tire. Türkçe karakter (ç, ş, ğ…) otomatik dönüştürülür.
                  </span>
                )}
              </p>
            </div>

            <div>
              <Label>{t("description")}</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label>{t("address")} <span className="text-destructive">*</span></Label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} required />
              </div>
              <div>
                <Label>{t("phone")}</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label>{t("website")}</Label>
                <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
              </div>
              <div>
                <Label>{t("instagram")}</Label>
                <Input
                  value={form.instagram}
                  onChange={(e) => setForm({ ...form, instagram: e.target.value })}
                  placeholder="@isletme"
                />
              </div>
            </div>
            <div className="w-40">
              <Label>{t("currency")}</Label>
              <select
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <option value="TRY">TRY (₺)</option>
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
                <option value="GBP">GBP (£)</option>
                <option value="RUB">RUB (₽)</option>
                <option value="SAR">SAR (﷼)</option>
                <option value="AED">AED (د.إ)</option>
                <option value="CHF">CHF (Fr)</option>
                <option value="JPY">JPY (¥)</option>
                <option value="KRW">KRW (₩)</option>
                <option value="CNY">CNY (¥)</option>
                <option value="INR">INR (₹)</option>
                <option value="BRL">BRL (R$)</option>
                <option value="CAD">CAD ($)</option>
                <option value="AUD">AUD ($)</option>
                <option value="SEK">SEK (kr)</option>
                <option value="NOK">NOK (kr)</option>
                <option value="DKK">DKK (kr)</option>
                <option value="PLN">PLN (zł)</option>
                <option value="CZK">CZK (Kč)</option>
                <option value="HUF">HUF (Ft)</option>
                <option value="RON">RON (lei)</option>
              </select>
            </div>
            <div className="flex items-center gap-3 rounded-lg border p-3">
              <Switch
                checked={waiterCallEnabled}
                onCheckedChange={(v) => setWaiterCallEnabled(v)}
              />
              <div>
                <Label>{t("waiter_call")}</Label>
                <p className="text-xs text-muted-foreground">
                  {t("waiter_call_desc")}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-lg border p-3">
              <Switch
                checked={orderEnabled}
                onCheckedChange={(v) => setOrderEnabled(v)}
              />
              <div>
                <Label>{t("order_enabled")}</Label>
                <p className="text-xs text-muted-foreground">
                  {t("order_enabled_desc")}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-lg border p-3">
              <Switch
                checked={loyaltyEnabled}
                onCheckedChange={(v) => setLoyaltyEnabled(v)}
              />
              <div>
                <Label>{t("loyalty_enabled")}</Label>
                <p className="text-xs text-muted-foreground">{t("loyalty_enabled_desc")}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-lg border p-3">
              <Switch
                checked={reviewsEnabled}
                onCheckedChange={(v) => setReviewsEnabled(v)}
              />
              <div>
                <Label>{t("reviews_enabled")}</Label>
                <p className="text-xs text-muted-foreground">{t("reviews_enabled_desc")}</p>
              </div>
            </div>
            <div className="rounded-lg border p-3">
              <div className="mb-2 flex items-center gap-2">
                <Volume2 className="size-4" />
                <Label>{t("notification_sounds")}</Label>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="mb-1 text-xs text-muted-foreground">{t("waiter_sound")}</p>
                  <div className="flex flex-wrap gap-2">
                    {(Object.entries(WAITER_SOUNDS) as [WaiterSoundKey, { labelKey: string }][]).map(([key, { labelKey }]) => (
                      <Badge
                        key={key}
                        variant={waiterSound === key ? "default" : "outline"}
                        className="cursor-pointer"
                        onClick={() => { setWaiterSound(key); playSound(WAITER_SOUNDS, key); }}
                      >
                        {t(labelKey as Parameters<typeof t>[0])}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="mb-1 text-xs text-muted-foreground">{t("order_sound")}</p>
                  <div className="flex flex-wrap gap-2">
                    {(Object.entries(ORDER_SOUNDS) as [OrderSoundKey, { labelKey: string }][]).map(([key, { labelKey }]) => (
                      <Badge
                        key={key}
                        variant={orderSound === key ? "default" : "outline"}
                        className="cursor-pointer"
                        onClick={() => { setOrderSound(key); playSound(ORDER_SOUNDS, key); }}
                      >
                        {t(labelKey as Parameters<typeof t>[0])}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <Button type="submit" disabled={updateMutation.isPending}>
              <Save className="mr-2 size-4" />
              {updateMutation.isPending ? t("saving") : t("save")}
            </Button>
            {updateMutation.isSuccess && (
              <span className="ml-3 text-sm text-primary">{t("saved")}</span>
            )}
          </form>
        </CardContent>
      </Card>

      {/* Crop Dialog */}
      <ImageCropDialog
        open={!!cropType}
        onClose={() => { setCropType(null); setCropSrc(""); }}
        imageSrc={cropSrc}
        aspectRatio={cropType === "logo" ? 1 : cropType === "cover" ? 2 : undefined}
        onCropped={handleCropped}
      />
    </div>
  );
}
